import {
	BasicButton,
	CardModal,
	Loading,
	PromoCode,
	RadioSubscription,
	SelectPlan,
	Typography,
} from "@/components";
import useProfile from "@/hooks/useProfile";
import { SUBSCRIPTION_STATUS } from "@/libraries/constant";
import { capitalized, isAuthenticating } from "@/libraries/helper";
import { STATUS } from "@/redux/constant";
import { postRegister, redirectUser } from "@/redux/reducers/auth";
import {
	applyPromotion,
	fetchPrePaymentData,
	proceedPayment,
	resetPromotion,
	resetSelectProduct,
	selectProduct,
} from "@/redux/reducers/payment";
import AssistantRoundedIcon from "@mui/icons-material/AssistantRounded";
import CasesRoundedIcon from "@mui/icons-material/CasesRounded";
import CheckIcon from "@mui/icons-material/Check";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import NewspaperRoundedIcon from "@mui/icons-material/NewspaperRounded";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import upperFirst from "lodash/upperFirst";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

export const Route = createFileRoute("/create-account/cookie-credentials")({
	beforeLoad: () => {
		if (!isAuthenticating()) {
			throw redirect({
				to: "/create-account",
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation(["auth", "nav"]);
	const dispatch = useDispatch();
	// initials form credentials
	const [formCredentials, setFormCredentials] = useReducer(
		(state: any, newState: any) => ({ ...state, ...newState }),
		{
			tos_agreement: false,
			privacy_policy_agreement: false,
			cookie_policy_agreement: false,
			patient_data_protection_policy: false,
			subscription_plan: "",
		},
	);
	const [subscriptionValue, setSubscriptionValue] = useState("");
	const [promoCode, setPromoCode] = useState("");
	const data = useSelector((state: any) => state.payment?.prePayment);
	const process = useSelector((state: any) => state.payment?.checkout);
	const promo = useSelector((state: any) => state.payment?.promotion);
	const payment = useSelector(
		(state: any) => state.payment?.prePayment?.data?.data,
	);
	const paymenStat = useSelector(
		(state: any) => state.payment?.prePayment?.data,
	);
	const paymentSelected = payment?.find(
		(subscription: { stripe_product_id: string }) =>
			subscription.stripe_product_id === subscriptionValue,
	);
	const [promoStatus, setPromoStatus] = useState(false);
	const [open, setOpen] = useState(false);
	const [buttonDisable, setButtonDisable] = useState(true);
	const [selectDisable, setSelectDisable] = useState(true);
	const [loading, setLoading] = useState(false);
	const [discDisable, setDiscDisable] = useState(false);
	const { profile } = useProfile();
	const isFailedSelectPlan = useSelector(
		(state: any) => state.payment?.selectProduct?.status === STATUS.FAILED,
	);
	const paymentStatStatus = useSelector(
		(state: any) => state.payment?.prePayment?.status,
	);

	useEffect(() => {
		if (data?.status === "failed") {
			toast.error(data?.error);
		}
	}, [data]);

	useEffect(() => {
		if (paymentStatStatus) {
			setLoading([STATUS.PENDING, STATUS.REQUEST].includes(paymentStatStatus));
		}
	}, [paymentStatStatus]);

	useEffect(() => {
		if (!subscriptionValue) {
			setDiscDisable(true);
		} else {
			setDiscDisable(false);
		}
	}, [subscriptionValue]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		// check input value
		setButtonDisable(
			!(
				formCredentials.subscription_plan &&
				formCredentials.tos_agreement === true &&
				formCredentials.patient_data_protection_policy === true &&
				formCredentials.cookie_policy_agreement === true &&
				formCredentials.privacy_policy_agreement === true
			),
		);
		if (process?.status !== "success") {
			setLoading(false);
		}
	}, [formCredentials, buttonDisable, process]);

	// const taxAmount = useMemo(() => {
	//   return paymenStat?.tax_amount || 0
	// }, [paymenStat?.tax_amount])

	const handlePromo = useCallback(() => {
		setLoading(true);
		dispatch(
			applyPromotion({
				code: promoCode,
				callback: () => {
					setPromoStatus(true);
					setLoading(false);
					dispatch(
						fetchPrePaymentData({
							tier: formCredentials.subscription_plan,
						}),
					);
				},
			}),
		);
		if (promo?.status !== "success") {
			setLoading(false);
		}
	}, [dispatch, promo?.status, promoCode, formCredentials.subscription_plan]);

	const handleChange = useCallback(
		(subscription: any) => {
			dispatch(
				selectProduct({
					tier: formCredentials.subscription_plan,
					product_id: subscription,
					callback: () => {
						setSubscriptionValue(subscription);
						setSelectDisable(false);
						if (promoStatus) {
							handlePromo();
						}
						dispatch(
							fetchPrePaymentData({
								tier: formCredentials.subscription_plan,
							}),
						);
					},
				}),
			);
		},
		[dispatch, handlePromo, promoStatus, formCredentials.subscription_plan],
	);

	const totalAmount = useMemo(() => {
		const amount =
			paymentSelected &&
			formCredentials.subscription_plan !== SUBSCRIPTION_STATUS.Free
				? paymentSelected?.amount || 0
				: 0;
		return `${paymenStat?.symbol_currency || "RM"}${amount}`;
	}, [paymenStat, formCredentials.subscription_plan, paymentSelected]);

	// checkbox
	const handleCheckboxChange = (event: any) => {
		const { name, checked } = event.target;
		setFormCredentials({ [name]: checked });
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: clear promo
	const handleClear = useCallback(() => {
		setPromoStatus(false);
	}, [promoStatus]);

	const handleSubmit = useCallback(() => {
		setLoading(true);
		dispatch(
			postRegister({
				id: profile?.id,
				tos_agreement: formCredentials.tos_agreement,
				privacy_policy_agreement: formCredentials.privacy_policy_agreement,
				cookie_policy_agreement: formCredentials.cookie_policy_agreement,
				// subscription_status: formCredentials.subscription_plan,
				patient_data_protection_policy:
					formCredentials.patient_data_protection_policy,
			}),
		);
		if (formCredentials.subscription_plan === "free") {
			setLoading(false);
			localStorage.removeItem("currentStep");
			localStorage.removeItem("formCredentials");
			localStorage.removeItem("phone_number");
			localStorage.removeItem("verify_code");
			dispatch(
				redirectUser({
					is_mobile: true,
					web_redirect: `${window.location.origin}/account`,
				}),
			);
		} else {
			dispatch(
				proceedPayment({
					callback: (link: string) => {
						setLoading(false);
						localStorage.removeItem("currentStep");
						localStorage.removeItem("formCredentials");
						localStorage.removeItem("phone_number");
						localStorage.removeItem("verify_code");
						window.location.href = link;
					},
				}),
			);
		}
	}, [dispatch, profile, formCredentials]);

	const onChangeSelectPlan = useCallback(
		(plan: string) => {
			if (plan !== SUBSCRIPTION_STATUS.Free) {
				dispatch(
					fetchPrePaymentData({
						tier: plan,
					}),
				);
			}
			setFormCredentials({
				subscription_plan: plan,
			});
			setOpen(true);
		},
		[dispatch],
	);

	useEffect(() => {
		if (formCredentials.subscription_plan === SUBSCRIPTION_STATUS.Free) {
			setSelectDisable(false);
		}
	}, [formCredentials.subscription_plan]);

	return (
		<>
			<div className="relative container flex-1 overflow-y-auto">
				<Box className="py-4">
					<Box className="bg-gray-light rounded-lg py-3 px-4">
						<p className="text-dark text-base mb-2">
							{t("auth:cookieCredentials.agreeIntro1")}
						</p>
						<p className="text-dark text-base">
							{t("auth:cookieCredentials.agreeIntro2")}
						</p>
					</Box>
				</Box>
				<Box className="wrapper py-2 mb-36">
					<p className="text-dark text-sm font-semibold py-2">
						{t("auth:cookieCredentials.checkboxIntro")}
					</p>
					<FormControl
						fullWidth
						sx={{
							"& .MuiFormControlLabel-label": {
								color: "#0072CE",
								fontSize: "14px",
								fontWeight: 600,
							},
						}}
					>
						<Box className="flex items-center -ml-2.5">
							<Checkbox
								name="tos_agreement"
								checked={formCredentials.tos_agreement}
								onChange={handleCheckboxChange}
								sx={{
									"&.Mui-checked": { color: "#0072CE" },
									"&:hover": { borderColor: "#0072CE" },
								}}
							/>
							<Link to="/term-service">
								<span className="text-sm text-primary font-bold">
									{t("nav:termsOfUse")}
								</span>
							</Link>
						</Box>
						<Box className="flex items-center -ml-2.5">
							<Checkbox
								name="privacy_policy_agreement"
								checked={formCredentials.privacy_policy_agreement}
								onChange={handleCheckboxChange}
								sx={{
									"&.Mui-checked": { color: "#0072CE" },
									"&:hover": { borderColor: "#0072CE" },
								}}
							/>
							<Link to="/privacy-policy">
								<span className="text-sm text-primary font-bold">
									{t("nav:privacyPolicy")}
								</span>
							</Link>
						</Box>
						<Box className="flex items-center -ml-2.5">
							<Checkbox
								name="patient_data_protection_policy"
								checked={formCredentials.patient_data_protection_policy}
								onChange={handleCheckboxChange}
								sx={{
									"&.Mui-checked": { color: "#0072CE" },
									"&:hover": { borderColor: "#0072CE" },
								}}
							/>
							<Link to="/patient-policy">
								<span className="text-sm text-primary font-bold">
									{t("nav:patientDataProtectionPolicy")}
								</span>
							</Link>
						</Box>
						<Box className="flex items-center -ml-2.5">
							<Checkbox
								name="cookie_policy_agreement"
								checked={formCredentials.cookie_policy_agreement}
								onChange={handleCheckboxChange}
								sx={{
									"&.Mui-checked": { color: "#0072CE" },
									"&:hover": { borderColor: "#0072CE" },
								}}
							/>
							<Link to="/cookie-policy">
								<span className="text-sm text-primary font-bold">
									{t("nav:cookiePolicy")}
								</span>
							</Link>
						</Box>
					</FormControl>
					<p className="text-dark text-sm font-semibold py-2 mt-4">
						{t("auth:cookieCredentials.selectPlan")}
					</p>
					<Box className="mt-2 md:w-96 mx-auto">
						<SelectPlan
							name="selectPlan"
							value={SUBSCRIPTION_STATUS.Free}
							checked={
								formCredentials.subscription_plan === SUBSCRIPTION_STATUS.Free
							}
							onChange={() => onChangeSelectPlan(SUBSCRIPTION_STATUS.Free)}
						>
							<Box>
								<h3 className="text-dark text-base font-semibold mb-0">
									{t("auth:cookieCredentials.planFree")}
								</h3>
								<p className="text-gray-100 text-sm">
									{t("auth:cookieCredentials.planFreeDesc")}
								</p>
							</Box>
						</SelectPlan>
						<SelectPlan
							name="selectPlan"
							value={SUBSCRIPTION_STATUS.PLUS}
							checked={
								formCredentials.subscription_plan === SUBSCRIPTION_STATUS.PLUS
							}
							plan={
								SUBSCRIPTION_STATUS.PLUS === formCredentials.subscription_plan
									? paymentSelected
									: undefined
							}
							subscriptionValue={subscriptionValue}
							totalAmount={totalAmount}
							onChange={() => onChangeSelectPlan(SUBSCRIPTION_STATUS.PLUS)}
						>
							<Box>
								<h3 className="text-dark text-base font-semibold mb-0">
									{t("auth:cookieCredentials.planPlus")}
								</h3>
								<p className="text-gray-100 text-sm">
									{t("auth:cookieCredentials.planPlusDesc")}
								</p>
							</Box>
						</SelectPlan>
						<SelectPlan
							name="selectPlan"
							value={SUBSCRIPTION_STATUS.PRO}
							checked={
								formCredentials.subscription_plan === SUBSCRIPTION_STATUS.PRO
							}
							plan={
								SUBSCRIPTION_STATUS.PRO === formCredentials.subscription_plan
									? paymentSelected
									: undefined
							}
							subscriptionValue={subscriptionValue}
							totalAmount={totalAmount}
							onChange={() => onChangeSelectPlan(SUBSCRIPTION_STATUS.PRO)}
						>
							<Box>
								<h3 className="text-dark text-base font-semibold mb-0">
									{t("auth:cookieCredentials.planPro")}
								</h3>
								<p className="text-gray-100 text-sm">
									{t("auth:cookieCredentials.planProDesc")}
								</p>
							</Box>
						</SelectPlan>
					</Box>
					<Typography fontWeight={500} fontSize={14}>
						{t("auth:cookieCredentials.enterpriseContact")}{" "}
						<Typography
							component="a"
							// @ts-expect-error
							href="mailto:contact@medplanner.io"
							className="text-primary"
						>
							contact@medplanner.io
						</Typography>
					</Typography>
				</Box>
				{/* button submit  */}
				<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<Box className="wrapper">
						<BasicButton
							variant="contained"
							color="secondary"
							onClick={handleSubmit}
							disable={buttonDisable}
							className="w-full !h-12 text-sm font-semibold !shadow-none"
						>
							{t("auth:cookieCredentials.next")}
						</BasicButton>
					</Box>
				</Box>
				{/* modal select plan */}
				<CardModal isOpen={open} onClose={() => setOpen(false)}>
					<h1 className="text-dark text-base font-bold mb-4">
						{t("auth:cookieCredentials.planModalTitle", {
							plan: capitalized(formCredentials.subscription_plan),
						})}
					</h1>
					<div className="py-2">
						<List dense className="!p-0">
							<ListItem className="!px-0 !py-2">
								<ListItemAvatar className="!min-w-16">
									<Avatar className="!bg-primary-light !w-12 !h-12">
										<CasesRoundedIcon className="text-primary" />
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={
										formCredentials.subscription_plan ===
										SUBSCRIPTION_STATUS.PLUS
											? t("auth:cookieCredentials.casesPlus")
											: formCredentials.subscription_plan ===
													SUBSCRIPTION_STATUS.PRO
												? t("auth:cookieCredentials.casesPro")
												: t("auth:cookieCredentials.casesFree")
									}
									secondary={
										formCredentials.subscription_plan ===
										SUBSCRIPTION_STATUS.PRO
											? t("auth:cookieCredentials.casesProSecondary")
											: t("auth:cookieCredentials.casesOtherSecondary")
									}
									classes={{
										primary: "!font-medium !text-sm",
										secondary: "!font-medium !text-xs",
									}}
								/>
							</ListItem>
							<ListItem className="!px-0 !py-2">
								<ListItemAvatar className="!min-w-16">
									<Avatar className="!bg-primary-light !w-12 !h-12">
										<NewspaperRoundedIcon className="text-primary" />
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={
										formCredentials.subscription_plan ===
										SUBSCRIPTION_STATUS.PLUS
											? t("auth:cookieCredentials.bulletinPlus")
											: formCredentials.subscription_plan ===
													SUBSCRIPTION_STATUS.PRO
												? t("auth:cookieCredentials.bulletinPro")
												: t("auth:cookieCredentials.bulletinFree")
									}
									secondary={
										formCredentials.subscription_plan ===
										SUBSCRIPTION_STATUS.PLUS
											? t("auth:cookieCredentials.bulletinPlusSecondary")
											: formCredentials.subscription_plan ===
													SUBSCRIPTION_STATUS.PRO
												? t("auth:cookieCredentials.bulletinProSecondary")
												: t("auth:cookieCredentials.bulletinFreeSecondary")
									}
									classes={{
										primary: "!font-medium !text-sm",
										secondary: "!font-medium !text-xs",
									}}
								/>
							</ListItem>
							<ListItem className="!px-0 !py-2">
								<ListItemAvatar className="!min-w-16">
									<Avatar className="!bg-primary-light !w-12 !h-12">
										<GroupsRoundedIcon className="text-primary" />
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={
										formCredentials.subscription_plan ===
										SUBSCRIPTION_STATUS.Free
											? t("auth:cookieCredentials.directoryFree")
											: t("auth:cookieCredentials.directoryOther")
									}
									secondary={
										formCredentials.subscription_plan ===
										SUBSCRIPTION_STATUS.Free
											? t("auth:cookieCredentials.directoryFreeSecondary")
											: t("auth:cookieCredentials.directoryOtherSecondary")
									}
									classes={{
										primary: "!font-medium !text-sm",
										secondary: "!font-medium !text-xs",
									}}
								/>
							</ListItem>
							<ListItem className="!px-0 !py-2">
								<ListItemAvatar className="!min-w-16">
									<Avatar className="!bg-primary-light !w-12 !h-12">
										<AssistantRoundedIcon className="text-primary" />
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={
										<span>
											<Typography
												component="span"
												fontWeight={500}
												fontSize={10}
												className="text-amber-600 p-1 rounded-md bg-amber-100"
											>
												{t("auth:cookieCredentials.comingSoon")}
											</Typography>
											<p>{t("auth:cookieCredentials.aiAssistant")}</p>
										</span>
									}
									secondary={
										formCredentials.subscription_plan ===
										SUBSCRIPTION_STATUS.Free
											? t("auth:cookieCredentials.aiQuestionsPerMonth", {
													count: "10",
												})
											: formCredentials.subscription_plan ===
													SUBSCRIPTION_STATUS.PLUS
												? t("auth:cookieCredentials.aiQuestionsPerMonth", {
														count: "50",
													})
												: t("auth:cookieCredentials.aiQuestionsUnlimited")
									}
									classes={{
										primary: "!font-medium !text-sm",
										secondary: "!font-medium !text-xs",
									}}
								/>
							</ListItem>
							{/* <ListItem className="!px-0 !py-2">
								<ListItemAvatar className="!min-w-16">
									<Avatar className="!bg-primary-light !w-12 !h-12">
										<SupportAgentRoundedIcon className="text-primary" />
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary="Priority customer support"
									secondary="Faster response time for your queries"
									classes={{
										primary: "!font-medium !text-sm",
										secondary: "!font-medium !text-xs",
									}}
								/>
							</ListItem> */}
						</List>
					</div>
					<div className="py-2">
						<Accordion variant="outlined" className="!bg-zinc-100 !rounded-xl">
							<AccordionSummary
								expandIcon={<ExpandMoreIcon className="!text-zinc-950" />}
								aria-controls="panel1-content"
								id="panel1-header"
								className="!h-12 !min-h-0"
							>
								<Typography component="span" fontWeight={500} fontSize={14}>
									{t("auth:cookieCredentials.essentialFeatures", {
										plan: upperFirst(formCredentials.subscription_plan),
									})}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<List dense={true} className="!py-0">
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary={t(
												"auth:cookieCredentials.featureUnlimitedChats",
											)}
											className="font-normal text-sm"
										/>
									</ListItem>
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary={t(
												"auth:cookieCredentials.featureUnlimitedTeams",
											)}
											className="font-normal text-sm"
										/>
									</ListItem>
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary={t("auth:cookieCredentials.featureTodoLists")}
											className="font-normal text-sm"
										/>
									</ListItem>
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary={t("auth:cookieCredentials.featureReminders")}
											className="font-normal text-sm"
										/>
									</ListItem>
									<ListItem className="!gap-2 !p-0">
										<ListItemIcon className="!block !min-w-0">
											<CheckIcon fontSize="small" className="!text-primary" />
										</ListItemIcon>
										<ListItemText
											primary={t(
												"auth:cookieCredentials.featureDirectoryVisibility",
											)}
											className="font-normal text-sm"
										/>
									</ListItem>
								</List>
							</AccordionDetails>
						</Accordion>
					</div>
					{payment?.length &&
					formCredentials.subscription_plan !== SUBSCRIPTION_STATUS.Free ? (
						<>
							<div className="py-2 flex flex-col gap-3 justify-center items-start">
								<Typography fontWeight={700} fontSize={14}>
									{t("auth:cookieCredentials.selectSubscription")}
								</Typography>
								<div className="w-full flex flex-col-reverse gap-3 justify-center items-start">
									{isFailedSelectPlan ? (
										<Box
											my={2}
											className="bg-danger-100 w-full flex items-start border border-danger-300 rounded-xl gap-2 p-4"
										>
											<ErrorRoundedIcon className="text-danger-300 !text-base" />
											<div>
												<p className="text-xs !leading-4 text-dark">
													{t("auth:cookieCredentials.selectPlanFailed")}
												</p>
											</div>
										</Box>
									) : null}
									{payment?.map((subscription: any) => (
										<RadioSubscription
											key={subscription.id}
											name="subscription"
											value={subscription.stripe_product_id}
											checked={
												subscriptionValue === subscription.stripe_product_id
											}
											onChange={handleChange}
										>
											<h3 className="text-dark text-2xl font-bold">
												{subscription.description}
											</h3>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
													fontSize: "10px",
												}}
											>
												{subscription.short_description && (
													<p className="text-gray-100 text-xs font-medium">
														{subscription.short_description}
													</p>
												)}
												{subscription.saved_to && (
													<button
														type="button"
														className="bg-primary text-white rounded py-0.5 px-1"
													>
														{t("auth:cookieCredentials.save", {
															amount: subscription.saved_to,
														})}
													</button>
												)}
											</Box>
										</RadioSubscription>
									))}
								</div>
							</div>
							<div className="py-2 flex flex-col gap-3 justify-center items-start">
								<Typography fontWeight={700} fontSize={14}>
									{t("auth:cookieCredentials.havePromoCode")}
								</Typography>
								<PromoCode
									value={promoCode}
									onChange={(e) => setPromoCode(e.target.value)}
									onClear={handleClear}
									onSubmit={handlePromo}
									disable={discDisable}
								/>
							</div>
						</>
					) : null}

					<div className="py-2 flex flex-col gap-3 justify-center items-start">
						<p className="text-dark text-sm font-bold py-2">
							{t("auth:cookieCredentials.summary")}
						</p>
						<Box
							sx={{
								width: "100%",
								display: "flex",
								flexDirection: "column",
								gap: 1,
								mb: 1,
							}}
						>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<p className="text-gray-100 text-xs">
									{formCredentials.subscription_plan ===
									SUBSCRIPTION_STATUS.Free
										? t("auth:cookieCredentials.freePlan")
										: t("auth:cookieCredentials.planBillingLine", {
												plan: capitalized(formCredentials.subscription_plan),
												billing:
													paymentSelected?.name ??
													t("auth:cookieCredentials.monthly"),
											})}
								</p>
								<p className="text-dark text-xs font-medium">
									{paymenStat?.symbol_currency || "RM"}
									{paymentSelected?.amount || 0}
								</p>
							</Box>

							{paymenStat?.tax_amount ? (
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<p className="text-gray-100 text-xs">{paymenStat.tax_name}</p>
									<p className="text-dark text-xs font-medium">
										{paymenStat?.symbol_currency || "RM"}
										{paymenStat.tax_amount}
									</p>
								</Box>
							) : null}
							{promoStatus && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<p className="text-gray-100 text-xs">
										{t("auth:cookieCredentials.promoCodeLine", {
											name: promo.data?.data?.promotion?.name,
										})}
									</p>
									<p className="text-danger-300 text-xs font-medium">
										-{paymenStat?.symbol_currency || "RM"}
										{promo.data?.data?.promotion?.amount}
									</p>
								</Box>
							)}
							<Box className="flex justify-between border-solid-top items-center pt-2 mb-4">
								<p className="text-gray-100 text-">
									{t("auth:cookieCredentials.total")}
								</p>
								<p className="text-dark text-sm font-medium">{totalAmount}</p>
							</Box>
						</Box>
					</div>

					<Box className="wrapper absolute w-full bg-white bottom-0 inset-x-0 z-30 py-3">
						<Box className="flex items-center gap-1 container">
							<BasicButton
								variant="outlined"
								color="secondary"
								className="w-full !h-12 !text-sm font-semibold !shadow-none"
								onClick={() => {
									setFormCredentials({
										subscription_plan: "",
									});
									setSubscriptionValue("");
									setPromoCode("");
									setOpen(false);
									setSelectDisable(true);
									dispatch(resetSelectProduct());
									dispatch(resetPromotion());
								}}
							>
								{t("auth:cookieCredentials.cancel")}
							</BasicButton>
							<BasicButton
								variant="contained"
								color="secondary"
								disable={selectDisable}
								onClick={() => {
									setOpen(false);
								}}
								className="w-full !h-12 !text-sm font-semibold !shadow-none"
							>
								{t("auth:cookieCredentials.select")}
							</BasicButton>
						</Box>
					</Box>
				</CardModal>
			</div>
			<Loading status={loading} />
		</>
	);
}
